























































































import {
  defineComponent,
  useContext,
  ref,
  watch,
  computed,
  useRouter,
  onMounted,
  useRoute,
} from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import { Figure } from '~/types/product/productTypes'
import ShowMore from '~/components/global/showmore/showMore.vue'
import IconFa6SolidAngleLeft from '~icons/fa6-solid/angle-left'
import IconFa6SolidAngleRight from '~icons/fa6-solid/angle-right'
import IconFa6SolidMaximize from '~icons/fa6-solid/maximize'
import { PLACEHOLDER_IMAGE_FILENAME } from '@/common/utils/product/constants'
import usePDPAnalytics from '@/composables/analytics/usePDPAnalytics'
import { useProductStore } from '@/store/product'
import { usePdpStore } from '@/store/pdp'
import { decodeHTMLEntities } from '~/common/utils/characters'

export default defineComponent({
  components: {
    ShowMore,
    IconFa6SolidAngleLeft,
    IconFa6SolidAngleRight,
    IconFa6SolidMaximize,
  },
  props: {
    images: {
      type: Array as () => Figure[],
      required: true,
    },
    useRelatedAndConjugatesUI: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const pdpStore = usePdpStore()
    const { $config } = useContext()
    const { trackCarouselView } = usePDPAnalytics()
    const { currentProduct } = storeToRefs(useProductStore())
    const { selectedApplication } = storeToRefs(pdpStore)
    const router = useRouter()
    const route = useRoute()
    const currentImage = ref(1)
    const carousel = ref()
    const slickOptions = {
      arrows: false,
      lazyLoad: 'ondemand',
    }

    const isMultipleImages = computed(() => {
      return props.images.length > 1
    })

    const isPlaceholderImage = (filename: string) => {
      return filename === PLACEHOLDER_IMAGE_FILENAME
    }

    const isCurrentImagePlaceholderImage = computed(() => {
      return isPlaceholderImage(props.images[currentImage.value - 1]?.filename)
    })

    const onlyPlaceholderImage = computed(() => {
      return !isMultipleImages.value && isCurrentImagePlaceholderImage.value
    })

    const buildSrc = (image: Figure) => {
      let url = `/${PLACEHOLDER_IMAGE_FILENAME}`
      if (isPlaceholderImage(image.filename)) {
        return url
      }

      url = `${$config.cst.mediaUrl}/product/image/${image.filename}`
      if (image.cloudflare?.variants.PUBLIC_MOD) {
        url = image.cloudflare.variants.PUBLIC_MOD
      }
      return url
    }

    const buildAlt = (image: Figure) => {
      let productName
      let fullNameForDisplay
      if (image.productName) {
        productName = decodeHTMLEntities(image.productName)
      }
      if (image.fullNameForDisplay) {
        fullNameForDisplay = decodeHTMLEntities(image.fullNameForDisplay)
      }
      if (isPlaceholderImage(image.filename)) {
        return 'No image available'
      }
      if (image.codeForDisplay === 'other') {
        return `${productName}: Image ${image.counter}`
      }
      return `${fullNameForDisplay} Image ${image.counter}: ${productName}`
    }

    const onBeforeChange = (_: number, newSlideIndex: number) => {
      currentImage.value = newSlideIndex + 1
      trackCarouselView()
    }

    const onArrowClick = (whereTo: string) => {
      whereTo === 'next' ? carousel.value.next() : carousel.value.prev()
    }

    onMounted(() => {
      const { index } = route.value.query
      if (index && +index <= props.images.length) {
        currentImage.value = +index || 1
        carousel.value?.goTo(currentImage.value - 1)
      }
    })

    watch(props, () => {
      currentImage.value = 1
      carousel.value?.goTo(0)
    })

    watch(selectedApplication, () => {
      const firstImageWithCodeAndProtocol = props.images.findIndex(
        (i) => i.code === selectedApplication.value.code && i.protocolId === selectedApplication.value.protocolId
      )
      if (firstImageWithCodeAndProtocol > 0) {
        carousel.value?.goTo(firstImageWithCodeAndProtocol)
      }
    })

    const handleRedirectToImageEnhancement = () => {
      router.push({
        path: route.value.path + '/applications',
        query: {
          index: currentImage.value.toString(),
          application: selectedApplication.value.code,
          type: 'pdp',
        },
      })
    }

    return {
      slickOptions,
      buildSrc,
      buildAlt,
      carousel,
      onBeforeChange,
      currentImage,
      onArrowClick,
      isMultipleImages,
      onlyPlaceholderImage,
      isPlaceholderImage,
      isCurrentImagePlaceholderImage,
      handleRedirectToImageEnhancement,
    }
  },
})
